/**
 * GNK 사업등록 시드
 * - 세부사업.xls (정책사업·단위사업·세부사업) → 정책 > 단위 > 세부 flat 행
 * - projectCode: 정책(01) / 단위(0101) / 세부(0101001)
 *
 * 재생성: node scripts/buildGnkProjectSeed.js
 */
import {
  cloneProjectPreset,
  validateProjectStructure,
  getConfirmedProjectStructure,
  loadProjectStructurePayload,
  saveProjectStructurePayload,
} from '../budgetProjectStructureStore';
import { loadProjects, saveProjects, STRUCTURE_BUDGET_TYPE } from '../budgetProjectStore';
import { ensureProjectsIdentity } from '../budgetProjectHistoryStore';
import { GNK_PROJECT_ROWS } from './gnkProjects.data';

export const GNK_PROJECT_SOURCE = 'GNK 사업 (세부사업.xls · 정책/단위/세부 매핑)';

export const GNK_TENANT_CODE = 'GNK';

export function isGnkTenant(tenantCode) {
  return String(tenantCode || '').trim().toUpperCase() === GNK_TENANT_CODE;
}

/** 샘플·미적재 시 GNK 시드로 교체할지 */
export function needsGnkProjectReseed(projects = []) {
  if (!projects.length) return true;
  if (projects.some((r) => String(r.remark || '').includes('GNK 사업'))) return false;
  // 데모 수돗물 샘플
  if (projects.some((r) => r.projectCode === '100000' && String(r.projectName || '').includes('수돗물'))) {
    return true;
  }
  return false;
}

export function getGnkProjectSeed(budgetType = STRUCTURE_BUDGET_TYPE) {
  const bt = budgetType || STRUCTURE_BUDGET_TYPE;
  const fy = String(new Date().getFullYear());
  return ensureProjectsIdentity(
    GNK_PROJECT_ROWS.map((r) => ({
      ...r,
      budgetType: bt,
      startYear: r.startYear || fy,
      endYear: r.endYear || '2100',
    }))
  );
}

/**
 * GNK 테넌트 — 사업구조 확정 + 사업 시드 적재
 */
export async function ensureGnkProjectBootstrap(dims, { userId } = {}) {
  const structureDims = {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    purpose: dims.purpose || '집행',
  };

  let confirmed = getConfirmedProjectStructure(structureDims);
  if (!confirmed) {
    const rows = cloneProjectPreset();
    const v = validateProjectStructure(rows);
    if (!v.canConfirm) {
      return { ok: false, reason: 'GNK 사업구조를 확정할 수 없습니다.', seedCount: 0 };
    }
    const fy = String(structureDims.fiscalYear || new Date().getFullYear());
    const prev = await loadProjectStructurePayload(structureDims);
    await saveProjectStructurePayload(structureDims, {
      dims: structureDims,
      rows,
      status: '확정',
      purpose: structureDims.purpose,
      usedLevels: v.used,
      finalLevelName: v.finalLevelName,
      applyLevelNo: v.applyLevelNo,
      applyLevelCode: v.applyLevelCode,
      applyLevelName: v.applyLevelName,
      levelColumns: [],
      previewText: v.previewText,
      versionNo: (Number(prev?.versionNo) || 0) + 1 || 1,
      confirmedAt: new Date().toISOString(),
      confirmedBy: userId || 'GNK-PROJECT-SEED',
      startDate: `${fy}-01-01`,
      endDate: `${fy}-12-31`,
      remark: GNK_PROJECT_SOURCE,
      savedAt: new Date().toISOString(),
      savedBy: userId || 'GNK-PROJECT-SEED',
    });
    confirmed = getConfirmedProjectStructure(structureDims);
  }

  if (!confirmed) {
    return { ok: false, reason: 'GNK 사업구조를 확정할 수 없습니다.', seedCount: 0 };
  }

  const d = { ...dims, purpose: structureDims.purpose };
  let projects = await loadProjects(d);
  let seeded = false;
  if (needsGnkProjectReseed(projects)) {
    projects = getGnkProjectSeed(STRUCTURE_BUDGET_TYPE);
    await saveProjects(d, projects);
    seeded = true;
  }

  return {
    ok: true,
    structure: confirmed,
    projects,
    seedCount: projects.length,
    seeded,
    source: GNK_PROJECT_SOURCE,
  };
}
